// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Loader, Paper, Stack, Table, Text, Title } from '@mantine/core';
import type { DiagnosticReport } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconExternalLink } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import { useRole } from '../../../hooks/useRole';
import { ROLES } from '../../../utils/permissions';

function getReportLabel(report: DiagnosticReport): string {
  const date = report.presentedForm?.[0]?.creation ?? report.effectiveDateTime ?? report.effectivePeriod?.start;
  if (!date) {
    return 'HSAT Results';
  }
  return `HSAT Results — ${dayjs(date).format('MMMM D, YYYY')}`;
}

function isReportViewable(status: DiagnosticReport['status']): boolean {
  return status === 'final' || status === 'amended' || status === 'corrected' || status === 'appended';
}

function getStatusLabel(status: DiagnosticReport['status']): string {
  const map: Record<string, string> = {
    final: 'Final',
    amended: 'Amended',
    corrected: 'Corrected',
    appended: 'Appended',
    partial: 'In Review',
    preliminary: 'In Review',
    registered: 'In Review',
    unknown: 'In Review',
    cancelled: 'Cancelled',
    'entered-in-error': 'Error',
  };
  return (status && map[status]) ?? 'In Review';
}

export function DiagnosticReportsPage(): JSX.Element {
  const { patientId } = useParams();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { role } = useRole();
  const isNursePractitioner = role === ROLES.NURSE_PRACTITIONER;

  const [reports, setReports] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    medplum
      .searchResources('DiagnosticReport', {
        subject: `Patient/${patientId}`,
        _sort: '-date',
        _count: '50',
      })
      .then((results) => {
        setReports(Array.from(results) as DiagnosticReport[]);
        setLoading(false);
      })
      .catch(() => {
        setReports([]);
        setLoading(false);
      });
  }, [medplum, patientId]);

  const openPdf = useCallback(
    async (report: DiagnosticReport) => {
      const rawUrl = report.presentedForm?.[0]?.url;
      if (!rawUrl) {
        return;
      }
      let href: string;
      if (rawUrl.startsWith('Binary/')) {
        const blob = await medplum.download(rawUrl);
        href = URL.createObjectURL(blob);
      } else {
        href = rawUrl;
      }
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    [medplum]
  );

  return (
    <Stack gap="lg" p="md">
      <Group justify="space-between">
        <Title order={2}>Diagnostic Reports</Title>
        <Button variant="subtle" onClick={() => navigate(`/Patient/${patientId}/medical-info`)}>
          Back to Medical Info
        </Button>
      </Group>

      <Paper withBorder radius="md" shadow="sm" p="xl">
        {loading && (
          <Stack align="center" py="md">
            <Loader size="sm" />
          </Stack>
        )}
        {!loading && reports.length === 0 && (
          <Text c="dimmed" size="sm">
            No diagnostic reports on file.
          </Text>
        )}
        {!loading && reports.length > 0 && (
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Report</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {reports.map((report) => (
                <Table.Tr key={report.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {getReportLabel(report)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{getStatusLabel(report.status)}</Text>
                  </Table.Td>
                  <Table.Td>
                    {report.presentedForm?.[0]?.url && (!isNursePractitioner || isReportViewable(report.status)) ? (
                      <TableAnchor
                        component="button"
                        type="button"
                        size="xs"
                        onClick={() => {
                          openPdf(report).catch(console.error);
                        }}
                      >
                        <Group gap={4} wrap="nowrap">
                          Open PDF
                          <IconExternalLink size={12} />
                        </Group>
                      </TableAnchor>
                    ) : (
                      <Text size="xs" c="dimmed">
                        No file
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
